import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  ArrowLeft, Building2, Users, HardHat, Briefcase, Settings,
  Ticket, FileText, MessageSquare, UserCog, Save, Loader2, ExternalLink,
  Link, Unlink, Search, CreditCard, KeyRound, Copy, Check
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

// StatCard moved outside component to prevent re-creation on each render
const StatCard = ({ title, value, icon: Icon, color = 'text-[#ffd108]', isDark = true }) => (
  <div className={`${isDark ? 'bg-slate-700/50' : 'bg-gray-100'} rounded-lg p-4`}>
    <div className="flex items-center gap-3">
      <Icon className={`h-5 w-5 ${color}`} />
      <div>
        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{title}</p>
        <p className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
      </div>
    </div>
  </div>
);

const TenantDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { api, admin } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [impersonateDialog, setImpersonateDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Password reset state
  const [passwordResetDialog, setPasswordResetDialog] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  
  // Stripe linking state
  const [stripeLinkDialog, setStripeLinkDialog] = useState(false);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  const [stripeSearchQuery, setStripeSearchQuery] = useState('');
  const [stripeSearchResults, setStripeSearchResults] = useState([]);
  const [stripeSearching, setStripeSearching] = useState(false);
  const [stripeLinking, setStripeLinking] = useState(false);
  
  // Check if current user is admin (not support agent)
  const isAdmin = admin?.role === 'admin';

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const res = await api.get(`/api/super-admin/tenants/${clientId}`);
        setTenant(res.data);
        setEditData({
          tier: res.data.tier || 'basic',
          subscription_status: res.data.subscription_status || 'active',
          extra_operators: res.data.extra_operators || 0,
          extra_users: res.data.extra_users || 0,
          ai_enabled: res.data.ai_enabled || false
        });
      } catch (err) {
        console.error('Failed to fetch tenant:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [api, clientId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/api/super-admin/tenants/${clientId}`, editData);
      setTenant(prev => ({ ...prev, ...editData }));
      setEditMode(false);
    } catch (err) {
      console.error('Failed to update tenant:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleImpersonate = async () => {
    if (!selectedUser) return;
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/impersonate?user_id=${selectedUser.id}`);
      // Open in new tab with impersonation token
      const url = `${window.location.origin}/dashboard?impersonate=${res.data.access_token}`;
      window.open(url, '_blank');
      setImpersonateDialog(false);
    } catch (err) {
      console.error('Failed to impersonate:', err);
    }
  };

  const handlePasswordReset = async () => {
    if (!passwordResetUser) return;
    setPasswordResetLoading(true);
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/users/${passwordResetUser.id}/reset-password`);
      setTempPasswordResult({
        username: res.data.username,
        password: res.data.temp_password,
        fullName: res.data.user_full_name
      });
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleStripeSearch = async () => {
    if (!stripeSearchQuery.trim()) return;
    setStripeSearching(true);
    try {
      const res = await api.get(`/api/super-admin/stripe/customers/search?query=${encodeURIComponent(stripeSearchQuery)}`);
      setStripeSearchResults(res.data.customers || []);
    } catch (err) {
      console.error('Failed to search Stripe customers:', err);
      setStripeSearchResults([]);
    } finally {
      setStripeSearching(false);
    }
  };

  const handleLinkStripe = async () => {
    if (!stripeCustomerId.trim()) return;
    setStripeLinking(true);
    try {
      const res = await api.post(`/api/super-admin/tenants/${clientId}/link-stripe`, {
        stripe_customer_id: stripeCustomerId
      });
      setTenant(prev => ({ ...prev, stripe_customer_id: stripeCustomerId }));
      setStripeLinkDialog(false);
      setStripeCustomerId('');
      setStripeSearchQuery('');
      setStripeSearchResults([]);
    } catch (err) {
      console.error('Failed to link Stripe customer:', err);
      alert(err.response?.data?.detail || 'Failed to link Stripe customer');
    } finally {
      setStripeLinking(false);
    }
  };

  const handleUnlinkStripe = async () => {
    if (!window.confirm('Are you sure you want to unlink this Stripe customer? The tenant will lose access to billing features.')) {
      return;
    }
    try {
      await api.delete(`/api/super-admin/tenants/${clientId}/link-stripe`);
      setTenant(prev => ({ ...prev, stripe_customer_id: null }));
    } catch (err) {
      console.error('Failed to unlink Stripe customer:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Tenant not found</p>
        <Button variant="ghost" onClick={() => navigate('/super-admin/tenants')} className="mt-4">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/super-admin/tenants')}>
          <ArrowLeft className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`} />
        </Button>
        <div className="flex-1">
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-3`}>
            <Building2 className="h-6 w-6 text-[#ffd108]" />
            {tenant.company_name}
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>Client ID: {tenant.client_id}</p>
        </div>
        <div className="flex items-center gap-2">
          {!editMode ? (
            <Button onClick={() => setEditMode(true)} variant="outline">
              <Settings className="mr-2 h-4 w-4" /> Edit
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard title="Users" value={tenant.stats?.users?.length || 0} icon={Users} color="text-blue-400" isDark={isDark} />
        <StatCard title="Operators" value={tenant.stats?.operators_count || 0} icon={HardHat} color="text-green-400" isDark={isDark} />
        <StatCard title="Jobs" value={tenant.stats?.jobs_count || 0} icon={Briefcase} color="text-purple-400" isDark={isDark} />
        <StatCard title="Jobs (30d)" value={tenant.stats?.jobs_this_month || 0} icon={Briefcase} color="text-orange-400" isDark={isDark} />
        <StatCard title="Chat Rooms" value={tenant.stats?.chat_rooms || 0} icon={MessageSquare} color="text-cyan-400" isDark={isDark} />
        <StatCard title="Form Submissions" value={tenant.stats?.form_submissions || 0} icon={FileText} color="text-pink-400" isDark={isDark} />
      </div>

      {/* Configuration */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardHeader>
          <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Tier</Label>
              {editMode ? (
                <Select value={editData.tier} onValueChange={(v) => setEditData(prev => ({ ...prev, tier: v }))}>
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div>
                  <Badge className="bg-[#ffd108] text-black">{tenant.tier || 'basic'}</Badge>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</Label>
              {editMode ? (
                <Select value={editData.subscription_status} onValueChange={(v) => setEditData(prev => ({ ...prev, subscription_status: v }))}>
                  <SelectTrigger className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="past_due">Past Due</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="trialing">Trialing</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div>
                  <Badge className="bg-green-500/20 text-green-400">{tenant.subscription_status || 'active'}</Badge>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Extra Operators</Label>
              {editMode ? (
                <Input
                  type="number"
                  value={editData.extra_operators}
                  onChange={(e) => setEditData(prev => ({ ...prev, extra_operators: parseInt(e.target.value) || 0 }))}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
              ) : (
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.extra_operators || 0}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Extra Users</Label>
              {editMode ? (
                <Input
                  type="number"
                  value={editData.extra_users}
                  onChange={(e) => setEditData(prev => ({ ...prev, extra_users: parseInt(e.target.value) || 0 }))}
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                />
              ) : (
                <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{tenant.extra_users || 0}</p>
              )}
            </div>
          </div>
          <div className={`mt-4 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
            <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
              Created: {tenant.created_at ? format(new Date(tenant.created_at), 'PPpp') : 'N/A'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Integration - Admin only */}
      {isAdmin && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={`${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
              <CreditCard className="h-5 w-5 text-[#ffd108]" />
              Stripe Integration
            </CardTitle>
            {tenant.stripe_customer_id ? (
              <Button size="sm" variant="outline" onClick={handleUnlinkStripe} className="text-red-500 hover:text-red-400">
                <Unlink className="mr-2 h-4 w-4" /> Unlink
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setStripeLinkDialog(true)}>
                <Link className="mr-2 h-4 w-4" /> Link Customer
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {tenant.stripe_customer_id ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-500/20 text-green-400">Linked</Badge>
                  <span className={`font-mono text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {tenant.stripe_customer_id}
                  </span>
                </div>
                <a 
                  href={`https://dashboard.stripe.com/customers/${tenant.stripe_customer_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
                >
                  View in Stripe Dashboard <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : (
              <div className={`text-center py-6 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No Stripe customer linked</p>
                <p className="text-sm mt-1">Link a Stripe customer to enable billing features</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Users */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className={isDark ? 'text-white' : 'text-gray-900'}>Users</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPasswordResetDialog(true)}>
              <KeyRound className="mr-2 h-4 w-4" /> Reset Password
            </Button>
            <Button size="sm" variant="outline" onClick={() => setImpersonateDialog(true)}>
              <UserCog className="mr-2 h-4 w-4" /> Impersonate
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className={isDark ? 'border-slate-700' : 'border-gray-200'}>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Name</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Username</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Role</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Last Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenant.stats?.users?.map((user) => (
                <TableRow key={user.id} className={isDark ? 'border-slate-700' : 'border-gray-100'}>
                  <TableCell className={isDark ? 'text-white' : 'text-gray-900'}>{user.full_name}</TableCell>
                  <TableCell className={isDark ? 'text-slate-400' : 'text-gray-500'}>{user.username}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{user.role}</Badge>
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {user.created_at ? formatDistanceToNow(new Date(user.created_at), { addSuffix: true }) : 'N/A'}
                  </TableCell>
                  <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {user.last_login ? formatDistanceToNow(new Date(user.last_login), { addSuffix: true }) : 'Never'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent Tickets */}
      {tenant.stats?.recent_tickets?.length > 0 && (
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Ticket className="h-5 w-5 text-orange-400" />
              Recent Tickets
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700' : 'border-gray-200'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Ticket</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Subject</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Priority</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenant.stats.recent_tickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className={`cursor-pointer ${isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'}`}
                    onClick={() => navigate(`/super-admin/tickets/${ticket.id}`)}
                  >
                    <TableCell className="text-[#ffd108] font-mono text-sm">{ticket.ticket_number}</TableCell>
                    <TableCell className={isDark ? 'text-white' : 'text-gray-900'}>{ticket.subject}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{ticket.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ticket.priority === 'urgent' ? 'destructive' : 'secondary'} className="capitalize">
                        {ticket.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                      {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Impersonate Dialog */}
      <Dialog open={impersonateDialog} onOpenChange={setImpersonateDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Impersonate User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              Select a user to impersonate. This will open a new tab logged in as that user.
            </p>
            <div className="space-y-2">
              {tenant.stats?.users?.map((user) => (
                <div
                  key={user.id}
                  onClick={() => setSelectedUser(user)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    selectedUser?.id === user.id
                      ? 'bg-[#ffd108]/20 border border-[#ffd108]'
                      : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.username} • {user.role}</p>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImpersonateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleImpersonate}
              disabled={!selectedUser}
              className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
            >
              <ExternalLink className="mr-2 h-4 w-4" /> Open as User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetDialog} onOpenChange={(open) => {
        setPasswordResetDialog(open);
        if (!open) {
          setPasswordResetUser(null);
          setTempPasswordResult(null);
        }
      }}>
        <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-md`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              <KeyRound className="inline mr-2 h-5 w-5 text-amber-500" />
              Reset User Password
            </DialogTitle>
          </DialogHeader>
          
          {!tempPasswordResult ? (
            <>
              <div className="space-y-4 py-4">
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Select a user to reset their password. A temporary password will be generated.
                </p>
                <div className="space-y-2">
                  {tenant?.stats?.users?.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => setPasswordResetUser(user)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        passwordResetUser?.id === user.id
                          ? 'bg-amber-500/20 border border-amber-500'
                          : isDark ? 'bg-slate-700/50 hover:bg-slate-700' : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.full_name}</p>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.username} • {user.role}</p>
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPasswordResetDialog(false)}>Cancel</Button>
                <Button
                  onClick={handlePasswordReset}
                  disabled={!passwordResetUser || passwordResetLoading}
                  className="bg-amber-500 text-black hover:bg-amber-600"
                >
                  {passwordResetLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...</>
                  ) : (
                    <><KeyRound className="mr-2 h-4 w-4" /> Reset Password</>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4 py-4">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                  <p className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                    Password reset successfully!
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-green-500' : 'text-green-600'}`}>
                    The user will be required to change this password on next login.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Username</p>
                    <p className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>{tempPasswordResult.username}</p>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Temporary Password</p>
                    <div className="flex items-center gap-2">
                      <code className={`flex-1 p-2 rounded font-mono text-lg ${isDark ? 'bg-slate-700 text-amber-400' : 'bg-gray-100 text-amber-600'}`}>
                        {tempPasswordResult.password}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(tempPasswordResult.password)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => {
                  setPasswordResetDialog(false);
                  setPasswordResetUser(null);
                  setTempPasswordResult(null);
                }} className="bg-[#ffd108] text-black hover:bg-[#e5bc07]">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Stripe Link Dialog - Admin only */}
      {isAdmin && (
        <Dialog open={stripeLinkDialog} onOpenChange={setStripeLinkDialog}>
          <DialogContent className={`${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'} max-w-lg`}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Link Stripe Customer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Search */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Search by email or name</Label>
                <div className="flex gap-2">
                  <Input
                    value={stripeSearchQuery}
                    onChange={(e) => setStripeSearchQuery(e.target.value)}
                    placeholder="customer@example.com"
                    className={isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}
                    onKeyDown={(e) => e.key === 'Enter' && handleStripeSearch()}
                  />
                  <Button onClick={handleStripeSearch} disabled={stripeSearching} variant="outline">
                    {stripeSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {stripeSearchResults.length > 0 && (
                <div className="space-y-2">
                  <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Search Results</Label>
                  <div className={`border rounded-md max-h-48 overflow-y-auto ${isDark ? 'border-slate-600' : 'border-gray-200'}`}>
                    {stripeSearchResults.map((customer) => (
                      <div
                        key={customer.id}
                        className={`p-3 cursor-pointer flex justify-between items-center ${
                          stripeCustomerId === customer.id 
                            ? (isDark ? 'bg-slate-700' : 'bg-blue-50') 
                            : (isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50')
                        } ${isDark ? 'border-b border-slate-600 last:border-0' : 'border-b border-gray-100 last:border-0'}`}
                        onClick={() => setStripeCustomerId(customer.id)}
                      >
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{customer.name || 'No name'}</p>
                          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{customer.email}</p>
                        </div>
                        <code className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{customer.id}</code>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Entry */}
              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-400' : 'text-gray-500'}>Or enter Customer ID directly</Label>
                <Input
                  value={stripeCustomerId}
                  onChange={(e) => setStripeCustomerId(e.target.value)}
                  placeholder="cus_xxxxxxxxxxxx"
                  className={isDark ? 'bg-slate-700 border-slate-600 text-white font-mono' : 'bg-gray-50 border-gray-200 text-gray-900 font-mono'}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => {
                setStripeLinkDialog(false);
                setStripeCustomerId('');
                setStripeSearchQuery('');
                setStripeSearchResults([]);
              }}>Cancel</Button>
              <Button
                onClick={handleLinkStripe}
                disabled={!stripeCustomerId || stripeLinking}
                className="bg-[#ffd108] text-black hover:bg-[#e5bc07]"
              >
                {stripeLinking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Link className="mr-2 h-4 w-4" />}
                Link Customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TenantDetail;
